/**
 * Browser-side IP geolocation.
 *
 * Round 40o.7 — fixes the broken-on-Emergent-ingress server-side
 * country detection. The backend was failing to resolve the user's
 * country (Cloudflare's `cf-ipcountry` header never reaches FastAPI
 * through Emergent's ingress, and ipapi.co called from the server
 * sees a private proxy IP, not the user's real IP). So users abroad
 * silently fell through to the `or 'IN'` default in
 * routes/auth.py:233 and got locked into INR + Razorpay forever.
 *
 * The browser sits on the user's real Wi-Fi — it can call a
 * geo-IP service directly from the user's real IP. That's a
 * different network path entirely, with a much higher success rate.
 *
 *   GET https://ipapi.co/json/   →  { "country_code": "GB", ... }
 *
 * We cache the result in localStorage for 7 days and attach it as
 * the `X-Detected-Country` header on every API request (see
 * lib/api.js). The backend reads it as the PRIMARY signal in
 * services/region.py — the existing server-side ladder remains as a
 * sanity-check fallback for users who block the API call or run
 * with localStorage disabled.
 *
 * Spoof risk: a malicious user could lie about their country to
 * route through a different currency. For region/pricing this is
 * acceptable — worst case someone fakes Indian for cheaper readings.
 * The DB still records the truth: any real payment exposes the
 * actual card-issuer country, and admin can flip residence_country
 * via PATCH /admin/users/{id} if needed.
 */

const STORAGE_KEY = 'astro_browser_country_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days
const FETCH_TIMEOUT_MS = 3000;

function _readCache() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const { country, ts } = JSON.parse(raw);
        if (!country || typeof country !== 'string') return null;
        if (Date.now() - Number(ts || 0) > TTL_MS) return null;
        return country;
    } catch {
        return null;
    }
}

function _writeCache(country) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            country, ts: Date.now(),
        }));
    } catch { /* localStorage unavailable — non-fatal */ }
}

/**
 * Synchronous cache read. Used by the axios request interceptor on
 * every API call — must NEVER do a network round-trip.
 *
 * Returns a 2-letter ISO country code (uppercase) or null.
 */
export function getCachedBrowserCountry() {
    return _readCache();
}

/**
 * Lazily resolve the browser's country via ipapi.co.
 *
 * Returns the cached value immediately if fresh. Otherwise fires
 * the lookup in the background, caches on success, returns the
 * resolved code (or null on any failure / timeout).
 *
 * Safe to call concurrently — duplicate in-flight calls share the
 * same promise via the module-level `_inflight` reference.
 */
let _inflight = null;
export async function resolveBrowserCountry() {
    const cached = _readCache();
    if (cached) return cached;
    if (_inflight) return _inflight;

    _inflight = (async () => {
        try {
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
            const res = await fetch('https://ipapi.co/json/', {
                signal: ctrl.signal,
                // Don't send cookies — third-party endpoint.
                credentials: 'omit',
            });
            clearTimeout(timer);
            if (!res.ok) return null;
            const data = await res.json();
            const code = String(data?.country_code || data?.country || '').toUpperCase();
            if (code.length === 2 && /^[A-Z]+$/.test(code)) {
                _writeCache(code);
                return code;
            }
            return null;
        } catch {
            // AbortError, network failure, JSON parse, CSP block — all fine.
            return null;
        } finally {
            _inflight = null;
        }
    })();
    return _inflight;
}

/**
 * Test-only escape hatch — lets jsdom-style tests preload the cache.
 */
export function _setBrowserCountryForTest(code) {
    if (code) _writeCache(String(code).toUpperCase());
    else {
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    }
}
